import React, {useRef, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {AuthProvider, useAuth} from './hooks/useAuth';
import LoginForm from './components/LoginForm';
import UserDashboard from './components/UserDashboard';
import WorkbookQuestions from '../components/modules/workbooks/WorkbookQuestions';
import AllWorkbooks from '../components/modules/workbooks/AllWorkbooks';
import {QueryClient, QueryClientProvider, useQueryClient} from '@tanstack/react-query';
import jwtDecode from 'jwt-decode';

const AppContent = ({viewType}) => {
    const {user, loading} = useAuth();
    const params = useParams();
    const workbookId = params.workbookId;

    if (loading) return <div>Loading...</div>;
    if (!user) {
        return (
            <div>
                <h2>Please sign in</h2>
                <LoginForm/>
            </div>
        );
    }

    // Show all workbooks list
    if (viewType === 'all_workbooks') {
        return <AllWorkbooks />;
    }

    // Show specific workbook
    if (workbookId && viewType === 'workbook') {
        return <WorkbookQuestions workbookId={workbookId}/>;
    }

    // Default view
    return (
        <div>
            <header>
                <h1>Welcome, {user.username}!</h1>
                Workbook ID needed to start.
            </header>
            <UserDashboard/>
        </div>
    );
};

const Embed = ({viewType = null}) => {
    // Get WordPress token and tenant ID from URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const wordpressToken = urlParams.get('wp_token');
    const tenantId = urlParams.get('tenant_id');
    const urlUserId = urlParams.get('user_id');
    const clearCache = urlParams.get('clear_cache');
    const timestamp = urlParams.get('_t');

    // Determine the current user ID to use as a key for forcing remounts
    // This is calculated synchronously before any state initialization
    let currentUserId = null;

    // Decode the WordPress token to get the user ID
    if (wordpressToken) {
        try {
            const decoded = jwtDecode(wordpressToken);
            currentUserId = String(decoded.user_id || decoded.sub);
        } catch (err) {
            console.error('Failed to decode WordPress token:', err);
        }
    }

    // Use URL user_id if available, otherwise use token user_id
    if (urlUserId) {
        currentUserId = String(urlUserId);
    }

    // Check on initial mount if we need to clear cache
    // This runs BEFORE the first render
    const initialClearDone = useRef(false);

    // Perform initial cache check synchronously before first render
    if (!initialClearDone.current) {
        // Check stored user ID from localStorage
        const storedUserId = localStorage.getItem('wp_user_id');

        // Determine if we need to clear cache
        // 1. WordPress explicitly signals clear_cache=1, OR
        // 2. User ID has changed (fallback detection)
        const shouldClearCache = clearCache === '1' ||
                                (storedUserId && currentUserId && storedUserId !== currentUserId);

        if (shouldClearCache) {
            // Clear ALL localStorage
            localStorage.clear();

            // Clear ALL sessionStorage
            sessionStorage.clear();
        }

        initialClearDone.current = true;
    }

    // Create QueryClient synchronously on initial render
    // This ensures a fresh QueryClient is created AFTER localStorage is cleared
    const [queryClient] = useState(() => {
        return new QueryClient({
            defaultOptions: {
                queries: {
                    staleTime: 0,
                    cacheTime: 0,
                    refetchOnMount: true,
                    refetchOnWindowFocus: false,
                },
            },
        });
    });

    // Use currentUserId as key to force complete remount when user changes
    // This ensures all child components are completely recreated with fresh state
    return (
        <QueryClientProvider client={queryClient} key={currentUserId}>
            <AuthProvider wordpressToken={wordpressToken} tenantId={tenantId} >
                <AppContent viewType={viewType}/>
            </AuthProvider>
        </QueryClientProvider>
    );
};

export default Embed;
